/**
 * Stage geometry → walk path.
 *
 * Engine-agnostic (no Phaser) so it unit-tests headlessly, like `Path.ts`. A
 * stage is authored as platforms (horizontal ledges) joined by stairwells; the
 * enemy walk path is *generated* from them so the lane is guaranteed to ride the
 * surfaces enemies stand on (or fly above). This is the single source of truth
 * for stage 1's serpentine staircase ("The Sundered Steps"): platforms alternate
 * left↔right down the world and each stairwell is the diagonal between one
 * platform's exit and the next platform's entry.
 *
 * NOTE: this generator handles a single linear chain (stage 1). Branching stages
 * (stage 2+) will need a multi-polyline path — out of scope here — but the
 * indexed stairwell `from`/`to` model below does not preclude it.
 */
import type { Vec2 } from '@/battle/Path';

/** Walk line sits this many px ABOVE a platform top so feet rest on the surface. */
export const FOOT_OFFSET = 14;
/** Walk endpoints inset from the platform corners so enemies don't stand on the edge. */
export const EDGE_INSET = 24;
/** How far below the world floor the final gate drop lands. */
const GATE_DROP = 80;

interface PlatformLike {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Optional explicit walk order; defaults to array index. */
  order?: number;
}
interface StairwellLike {
  from: number;
  to: number;
  steps?: number;
}
interface GeoLevel {
  platforms: PlatformLike[];
  stairwells: StairwellLike[];
  world: { width: number; height: number };
}

/**
 * Order platforms into a single walk chain. If stairwells are present we follow
 * them from the head (a platform that is never a stairwell's `to`); otherwise we
 * fall back to `order ?? index`. Any platform not reached is appended for safety.
 */
function walkOrder(platforms: PlatformLike[], stairwells: StairwellLike[]): number[] {
  const byOrder = platforms
    .map((_, i) => i)
    .sort((a, b) => (platforms[a].order ?? a) - (platforms[b].order ?? b));
  if (stairwells.length === 0) return byOrder;

  const toSet = new Set(stairwells.map((s) => s.to));
  const next = new Map(stairwells.map((s) => [s.from, s.to]));
  const head = byOrder.find((i) => !toSet.has(i)) ?? byOrder[0];

  const order: number[] = [];
  const seen = new Set<number>();
  let cur: number | undefined = head;
  while (cur !== undefined && !seen.has(cur)) {
    order.push(cur);
    seen.add(cur);
    cur = next.get(cur);
  }
  for (const i of byOrder) if (!seen.has(i)) order.push(i); // safety: orphans
  return order;
}

/** Entry/exit corners of one platform along the walk, at the surface (top) y. */
interface PlatformWalk {
  index: number;
  entryX: number;
  exitX: number;
  /** Surface (platform top) y. The walk line sits FOOT_OFFSET above this. */
  topY: number;
}

/**
 * Resolve the per-platform walk corners in traversal order. The walk alternates
 * sides each platform (serpentine switchback), entering the first platform from
 * its left/spawn side. Shared by both the path and the stairwell renderer so the
 * two never drift.
 */
function platformWalks(level: GeoLevel): PlatformWalk[] {
  const { platforms, stairwells } = level;
  const order = walkOrder(platforms, stairwells);
  let entryLeft = true;
  return order.map((index) => {
    const p = platforms[index];
    const left = p.x + EDGE_INSET;
    const right = p.x + p.w - EDGE_INSET;
    const walk: PlatformWalk = {
      index,
      entryX: entryLeft ? left : right,
      exitX: entryLeft ? right : left,
      topY: p.y,
    };
    entryLeft = !entryLeft; // switchback on the next platform
    return walk;
  });
}

/**
 * Build the enemy walk path from platforms + stairwells. Each platform yields
 * two waypoints on its walk line (entry edge → exit edge); the segment between
 * one platform's exit and the next's entry IS the stairwell (a descending
 * diagonal that `Path` interpolates for free). A final waypoint drops straight
 * off the last step into the gate.
 *
 * Result length is `2 * platforms.length + 1`; `[0]` is the spawn, the last is
 * the gate.
 */
export function generatePathFromGeometry(level: GeoLevel): Vec2[] {
  if (level.platforms.length === 0) {
    throw new Error('generatePathFromGeometry: stage has no platforms');
  }
  const pts: Vec2[] = [];
  for (const w of platformWalks(level)) {
    const wy = w.topY - FOOT_OFFSET;
    pts.push({ x: w.entryX, y: wy }); // entry
    pts.push({ x: w.exitX, y: wy }); // exit
  }
  // Drop off the last step into the gate, below the world floor.
  const exit = pts[pts.length - 1];
  pts.push({ x: exit.x, y: level.world.height - GATE_DROP });
  return pts;
}

/** One stairwell to render: the diagonal from a platform's exit corner down to
 *  the next platform's entry corner, both at surface (top) y. */
export interface StairSegment {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  steps: number;
}

/**
 * The stairwells as drawable diagonal segments, in walk order. Endpoints are the
 * platform-top corners (same corners the path uses, minus the foot offset) so the
 * rendered steps line up exactly under the lane. `steps` comes from the authored
 * stairwell (default 6) when present, matched by from/to indices.
 */
export function stairwellSegments(level: GeoLevel): StairSegment[] {
  const walks = platformWalks(level);
  const stepsFor = new Map(level.stairwells.map((s) => [`${s.from}->${s.to}`, s.steps ?? 6]));
  const segs: StairSegment[] = [];
  for (let i = 0; i < walks.length - 1; i++) {
    const a = walks[i];
    const b = walks[i + 1];
    segs.push({
      ax: a.exitX,
      ay: a.topY,
      bx: b.entryX,
      by: b.topY,
      steps: stepsFor.get(`${a.index}->${b.index}`) ?? 6,
    });
  }
  return segs;
}
